import { Button, Card, Dropdown, Text } from '@ui/themed';
import { t } from 'i18next';
import { memo, useMemo } from 'react';
import { SettingToggleRow } from '../../../components/setting-toggle-row';
import type { I18nPrefix } from '../../../i18n/prefix';
import {
  ANALYTICS_EVENT_NAME_KEYS,
  ANALYTICS_EVENT_NAMES,
  type AnalyticsEventName,
  type AnalyticsOptions,
  type AnalyticsProviderConfig,
  isOfficialAnalyticsProviderName,
  OFFICIAL_ANALYTICS_PROVIDERS,
  type OfficialAnalyticsProviderName,
} from '../types/spark-options';

const EDITOR =
  'features:storefront_options.components.analytics_providers_editor' satisfies I18nPrefix;
function createDefaultEvents(): Partial<Record<AnalyticsEventName, boolean>> {
  return Object.fromEntries(ANALYTICS_EVENT_NAME_KEYS.map((eventName) => [eventName, false]));
}

function createDefaultProvider(handler: OfficialAnalyticsProviderName): AnalyticsProviderConfig {
  return {
    handler,
    events: createDefaultEvents(),
  };
}

function handlerLabel(handler: OfficialAnalyticsProviderName): string {
  return t(`${EDITOR}.official_providers.${handler}`);
}

function firstUnusedOfficialProvider(
  providers: AnalyticsProviderConfig[],
): OfficialAnalyticsProviderName | null {
  const usedOfficialProviders = new Set(providers.map((provider) => provider.handler));
  return (
    OFFICIAL_ANALYTICS_PROVIDERS.find((providerName) => !usedOfficialProviders.has(providerName)) ??
    null
  );
}

function eventLabel(eventName: AnalyticsEventName): string {
  return t(`${EDITOR}.events.${ANALYTICS_EVENT_NAMES[eventName]}`);
}

function AnalyticsProviderRow({
  provider,
  unavailableOfficialProviders,
  onChange,
  onRemove,
}: {
  provider: AnalyticsProviderConfig;
  unavailableOfficialProviders: Set<OfficialAnalyticsProviderName>;
  onChange: (provider: AnalyticsProviderConfig) => void;
  onRemove: () => void;
}) {
  const handlerOptions = OFFICIAL_ANALYTICS_PROVIDERS.map((providerName) => ({
    value: providerName,
    label: handlerLabel(providerName),
    disabled: unavailableOfficialProviders.has(providerName),
  }));
  const title = handlerLabel(provider.handler);

  function updateEvent(eventName: AnalyticsEventName, checked: boolean) {
    onChange({
      ...provider,
      events: {
        ...provider.events,
        [eventName]: checked,
      },
    });
  }

  return (
    <Card size="sm">
      <Card.Header
        headingLevel="h3"
        collapsible
        badges={[{ text: t(`${EDITOR}.official_badge`), variant: 'blue' }]}
        primaryAction={{
          variant: 'dangerBare',
          icon: 'delete',
          ariaLabel: t(`${EDITOR}.remove_provider`),
          onClick: onRemove,
        }}
      >
        {title}
      </Card.Header>

      <Card.Body>
        <Dropdown
          label={t(`${EDITOR}.handler`)}
          options={handlerOptions}
          value={provider.handler}
          onChange={(value) => {
            if (
              isOfficialAnalyticsProviderName(value) &&
              !unavailableOfficialProviders.has(value)
            ) {
              onChange({ ...provider, handler: value });
            }
          }}
        />

        <div className="flex flex-col gap-2">
          <Text size="sm" bold>
            {t(`${EDITOR}.events_label`)}
          </Text>
          <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
            {ANALYTICS_EVENT_NAME_KEYS.map((eventName) => (
              <SettingToggleRow
                key={eventName}
                label={eventLabel(eventName)}
                description={eventName}
                checked={provider.events[eventName] === true}
                onChange={(checked) => updateEvent(eventName, checked)}
                mono
              />
            ))}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export const AnalyticsProvidersEditor = memo(function AnalyticsProvidersEditor({
  value,
  onChange,
}: {
  value: AnalyticsOptions | null;
  onChange: (value: AnalyticsOptions | null) => void;
}) {
  const providers = Array.isArray(value?.providers) ? value.providers : [];
  const nextProviderHandler = firstUnusedOfficialProvider(providers);

  const rows = useMemo(
    () =>
      providers.map((provider, index) => ({
        provider,
        unavailableOfficialProviders: new Set(
          providers.filter((_, i) => i !== index).map((other) => other.handler),
        ),
      })),
    [providers],
  );

  function commitProviders(nextProviders: AnalyticsProviderConfig[]) {
    onChange(nextProviders.length > 0 ? { providers: nextProviders } : null);
  }

  function updateAt(index: number, provider: AnalyticsProviderConfig) {
    commitProviders(providers.map((current, i) => (i === index ? provider : current)));
  }

  function removeAt(index: number) {
    commitProviders(providers.filter((_, i) => i !== index));
  }

  function addProvider() {
    if (nextProviderHandler === null) {
      return;
    }

    commitProviders([...providers, createDefaultProvider(nextProviderHandler)]);
  }

  return (
    <div className="flex flex-col gap-3">
      {providers.length === 0 && (
        <Text size="sm" variant="secondary">
          {t(`${EDITOR}.empty`)}
        </Text>
      )}

      {rows.map(({ provider, unavailableOfficialProviders }, index) => (
        <AnalyticsProviderRow
          // biome-ignore lint/suspicious/noArrayIndexKey: providers are positional and have no stable id
          key={index}
          provider={provider}
          unavailableOfficialProviders={unavailableOfficialProviders}
          onChange={(next) => updateAt(index, next)}
          onRemove={() => removeAt(index)}
        />
      ))}

      <div>
        <Button
          variant="secondary"
          icon="plusCircle"
          disabled={nextProviderHandler === null}
          onClick={addProvider}
        >
          {t(`${EDITOR}.add_provider`)}
        </Button>
      </div>
    </div>
  );
});
